import React, { FormEvent, useEffect, useState } from "react";
import { ReactComponent as PercentLogo } from "../../../assets/images/percentMapLogo.svg";
import type { Lang } from "../i18n";

const SUPPORT_EMAIL = "support@percentmap.com";

const copy = {
    ko: {
        supportTitle: "고객 지원",
        supportIntro: "Percent Map 이용 중 도움이 필요하시면 아래 채널로 문의해 주세요.",
        supportCards: [
            ["이메일 문의", "계정, 개인정보 열람·정정, 신고 및 서비스 이용에 관한 모든 문의를 접수합니다."],
            ["계정 삭제", "앱을 사용할 수 없는 경우에도 웹에서 계정 삭제를 요청할 수 있습니다."],
        ],
        deletionTitle: "계정 및 데이터 삭제 요청",
        deletionIntro:
            "앱을 삭제했거나 로그인할 수 없는 경우 이 페이지에서 Percent Map 계정 삭제를 요청할 수 있습니다.",
        processTitle: "처리 절차",
        process: [
            "아래 양식으로 요청을 제출합니다.",
            "입력한 이메일을 통해 계정 소유 여부를 확인할 수 있습니다.",
            "본인 확인이 완료되면 7일 이내 계정과 연결 데이터를 삭제합니다.",
            "처리 결과를 입력한 이메일로 안내합니다.",
        ],
        deletedTitle: "삭제되는 데이터",
        deleted:
            "프로필, 이메일, 위치, 사진, 푸시 토큰과 앱 DB의 게시물·댓글 등 계정에 연결된 데이터가 삭제됩니다.",
        retainedTitle: "예외 및 보관 기간",
        retained:
            "백업은 최대 30일, 접속·보안 로그는 최대 90일, 삭제 요청 및 처리 기록은 1년 보관됩니다. Hive·Steem 블록체인에 이미 기록된 데이터는 기술적으로 삭제되지 않을 수 있습니다.",
        email: "회신 이메일",
        username: "Percent Map 계정명",
        provider: "로그인 방식",
        providerPlaceholder: "로그인 방식을 선택해 주세요",
        details: "추가 내용 (선택)",
        consent:
            "계정 확인과 삭제 요청 처리를 위해 위 정보를 수집하고 삭제 요청 기록을 1년간 보관하는 데 동의합니다.",
        submit: "계정 삭제 요청",
        sending: "요청 전송 중...",
        success: "삭제 요청이 접수되었습니다. 본인 확인 및 처리 안내를 이메일로 보내드리겠습니다.",
        error: "요청을 전송하지 못했습니다. 잠시 후 다시 시도하거나 이메일로 문의해 주세요.",
        required: "필수 항목과 동의 여부를 확인해 주세요.",
        back: "홈으로",
        privacy: "개인정보처리방침",
        terms: "이용약관",
        support: "고객 지원",
        deletion: "계정 삭제",
        openInquiry: "문의 작성하기",
        openService: "이동하기",
    },
    en: {
        supportTitle: "Customer Support",
        supportIntro: "Contact us through the channels below if you need help with Percent Map.",
        supportCards: [
            ["Email support", "Questions about accounts, privacy access or correction, reports, and use of the Service."],
            ["Account deletion", "Request account deletion on the web even if you cannot use the app."],
        ],
        deletionTitle: "Account and Data Deletion Request",
        deletionIntro:
            "If you uninstalled the app or cannot sign in, you can request deletion of your Percent Map account here.",
        processTitle: "How it works",
        process: [
            "Submit the form below.",
            "We may contact the email provided to verify account ownership.",
            "After verification, we delete the account and associated data within seven days.",
            "We send the result to the email provided.",
        ],
        deletedTitle: "Data deleted",
        deleted:
            "We delete account-linked data including profile, email, location, photos, push tokens, posts, and comments in the app database.",
        retainedTitle: "Exceptions and retention",
        retained:
            "Backups are retained for up to 30 days, access and security logs for up to 90 days, and deletion request records for one year. Data already recorded on the Hive or Steem blockchain may not be technically removable.",
        email: "Reply email",
        username: "Percent Map username",
        provider: "Sign-in method",
        providerPlaceholder: "Select your sign-in method",
        details: "Additional details (optional)",
        consent:
            "I consent to the collection of the information above to verify and process this request and to retention of the deletion request record for one year.",
        submit: "Request account deletion",
        sending: "Sending request...",
        success: "Your request has been received. We will email you about verification and completion.",
        error: "We could not send your request. Please try again later or contact us by email.",
        required: "Please complete the required fields and consent.",
        back: "Back to Home",
        privacy: "Privacy Policy",
        terms: "Terms of Service",
        support: "Support",
        deletion: "Account deletion",
        openInquiry: "Write an inquiry",
        openService: "Open",
    },
};

function useLanguage() {
    const [lang, setLangState] = useState<Lang>("ko");

    useEffect(() => {
        const saved = localStorage.getItem("pm_lang");
        setLangState(saved === "en" ? "en" : "ko");
    }, []);

    const setLang = (next: Lang) => {
        localStorage.setItem("pm_lang", next);
        setLangState(next);
    };

    return { lang, setLang };
}

function PageFrame({
    lang,
    setLang,
    children,
}: {
    lang: Lang;
    setLang: (lang: Lang) => void;
    children: React.ReactNode;
}) {
    const t = copy[lang];
    return (
        <div className="min-h-screen w-full bg-zinc-950 text-white">
            <header className="sticky top-0 z-40 border-b border-white/10 bg-zinc-950/80 backdrop-blur">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:px-6">
                    <a href="/" className="flex items-center gap-2">
                        <PercentLogo className="h-8 w-8" />
                        <span className="font-brand text-2xl font-semibold">Percent Map</span>
                    </a>
                    <div className="flex rounded-xl border border-white/10 bg-white/5 p-1 text-xs font-semibold">
                        {(["ko", "en"] as Lang[]).map((item) => (
                            <button
                                key={item}
                                type="button"
                                onClick={() => setLang(item)}
                                className={`rounded-lg px-3 py-1.5 ${
                                    lang === item ? "bg-white text-zinc-950" : "text-white/70"
                                }`}
                            >
                                {item === "ko" ? "KOR" : "ENG"}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            {children}

            <footer className="border-t border-white/10">
                <div className="mx-auto flex max-w-6xl flex-col gap-5 px-4 py-10 text-sm text-white/65 md:px-6">
                    <nav className="flex flex-wrap gap-x-5 gap-y-2">
                        <a href="/">← {t.back}</a>
                        <a href="/privacy">{t.privacy}</a>
                        <a href="/terms">{t.terms}</a>
                        <a href="/support">{t.support}</a>
                        <a href="/account-deletion">{t.deletion}</a>
                    </nav>
                    <div className="flex flex-col gap-1 border-t border-white/10 pt-5 md:flex-row md:justify-between">
                        <span>© {new Date().getFullYear()} Percent Map. All rights reserved.</span>
                        <span>Operator: John Kim · {SUPPORT_EMAIL}</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export function SupportPage() {
    const { lang, setLang } = useLanguage();
    const t = copy[lang];
    const supportLinks = ["/#contact", "/account-deletion"];

    return (
        <PageFrame lang={lang} setLang={setLang}>
            <main className="mx-auto max-w-5xl px-4 py-14 md:px-6 md:py-20">
                <div className="text-center">
                    <div className="text-xs tracking-[0.22em] text-white/45">SUPPORT</div>
                    <h1 className="mt-3 text-3xl font-semibold md:text-4xl">{t.supportTitle}</h1>
                    <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-white/65">{t.supportIntro}</p>
                </div>

                <div className="mt-12 grid gap-4 md:grid-cols-2">
                    {t.supportCards.map(([title, body], index) => (
                        <a
                            key={title}
                            href={supportLinks[index]}
                            className="group rounded-3xl border border-white/10 bg-white/5 p-6 transition hover:-translate-y-0.5 hover:border-white/25 hover:bg-white/10"
                        >
                            <h2 className="font-semibold">{title}</h2>
                            <p className="mt-3 text-sm leading-6 text-white/65">{body}</p>
                            <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-cyan-200">
                                <span>{index === 1 ? t.deletion : t.openInquiry}</span>
                                <span className="transition-transform group-hover:translate-x-1">→</span>
                            </div>
                        </a>
                    ))}
                </div>

                <div className="mt-8 rounded-3xl border border-cyan-300/20 bg-cyan-300/5 p-7 text-center">
                    <p className="text-sm text-white/65">Email</p>
                    <a className="mt-2 inline-block text-lg font-semibold underline underline-offset-4" href={`mailto:${SUPPORT_EMAIL}`}>
                        {SUPPORT_EMAIL}
                    </a>
                    <div className="mt-6">
                        <a className="inline-flex rounded-xl bg-white px-5 py-3 text-sm font-semibold text-zinc-950" href="/#contact">
                            {t.openInquiry}
                        </a>
                    </div>
                </div>
            </main>
        </PageFrame>
    );
}

type RequestStatus = "idle" | "sending" | "success" | "error";

export function AccountDeletionPage() {
    const { lang, setLang } = useLanguage();
    const t = copy[lang];
    const [status, setStatus] = useState<RequestStatus>("idle");
    const [message, setMessage] = useState("");
    const [form, setForm] = useState({
        email: "",
        username: "",
        provider: "",
        details: "",
        website: "",
        consent: false,
    });

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setMessage("");
        if (!form.email.trim() || !form.username.trim() || !form.provider || !form.consent) {
            setMessage(t.required);
            return;
        }
        if (form.website) return;

        setStatus("sending");
        try {
            const response = await fetch("/api/services/contact/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: form.email.trim(),
                    subject: "[Account deletion request] Percent Map",
                    content: [
                        `Username: ${form.username.trim()}`,
                        `Sign-in method: ${form.provider}`,
                        `Language: ${lang}`,
                        `Details: ${form.details.trim() || "(none)"}`,
                        "Retention consent: accepted",
                    ].join("\n"),
                }),
            });
            const result = await response.json();
            if (!response.ok || result?.ok !== true) throw new Error(result?.error || "Request failed");
            setStatus("success");
        } catch (_) {
            setStatus("error");
        }
    };

    const inputClass =
        "mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none placeholder:text-white/30 focus:border-cyan-300/50";

    return (
        <PageFrame lang={lang} setLang={setLang}>
            <main className="mx-auto max-w-5xl px-4 py-14 md:px-6 md:py-20">
                <div className="text-center">
                    <div className="text-xs tracking-[0.22em] text-white/45">ACCOUNT DELETION</div>
                    <h1 className="mt-3 text-3xl font-semibold md:text-4xl">{t.deletionTitle}</h1>
                    <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-white/65">{t.deletionIntro}</p>
                </div>

                <div className="mt-12 grid gap-5 lg:grid-cols-2">
                    <div className="space-y-5">
                        <section className="rounded-3xl border border-white/10 bg-white/5 p-6">
                            <h2 className="font-semibold">{t.processTitle}</h2>
                            <ol className="mt-4 space-y-3 text-sm leading-6 text-white/65">
                                {t.process.map((item, index) => <li key={item}>{index + 1}. {item}</li>)}
                            </ol>
                        </section>
                        <section className="rounded-3xl border border-white/10 bg-white/5 p-6">
                            <h2 className="font-semibold">{t.deletedTitle}</h2>
                            <p className="mt-3 text-sm leading-6 text-white/65">{t.deleted}</p>
                        </section>
                        <section className="rounded-3xl border border-amber-300/20 bg-amber-300/5 p-6">
                            <h2 className="font-semibold">{t.retainedTitle}</h2>
                            <p className="mt-3 text-sm leading-6 text-white/65">{t.retained}</p>
                        </section>
                    </div>

                    <section className="rounded-3xl border border-white/10 bg-white/5 p-6 md:p-7">
                        {status === "success" ? (
                            <div className="flex min-h-[420px] flex-col items-center justify-center text-center">
                                <div className="text-3xl">✓</div>
                                <p className="mt-4 max-w-sm text-sm leading-7 text-white/70">{t.success}</p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-5">
                                <label className="block text-sm text-white/70">{t.email} *
                                    <input type="email" maxLength={200} className={inputClass} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                                </label>
                                <label className="block text-sm text-white/70">{t.username} *
                                    <input maxLength={100} className={inputClass} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required />
                                </label>
                                <label className="block text-sm text-white/70">{t.provider} *
                                    <select className={inputClass} value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} required>
                                        <option value="" className="text-zinc-950">{t.providerPlaceholder}</option>
                                        {["Email", "Google", "Apple", "Facebook"].map((item) => <option className="text-zinc-950" key={item}>{item}</option>)}
                                    </select>
                                </label>
                                <label className="block text-sm text-white/70">{t.details}
                                    <textarea maxLength={2000} rows={4} className={inputClass} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} />
                                </label>
                                <input aria-hidden tabIndex={-1} autoComplete="off" className="hidden" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
                                <label className="flex items-start gap-3 text-sm leading-6 text-white/65">
                                    <input type="checkbox" className="mt-1" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} />
                                    <span>{t.consent}</span>
                                </label>
                                {message && <p className="text-sm text-amber-300">{message}</p>}
                                {status === "error" && <p className="text-sm text-red-300">{t.error}</p>}
                                <button disabled={status === "sending"} className="w-full rounded-xl bg-white px-5 py-3 text-sm font-semibold text-zinc-950 disabled:opacity-50">
                                    {status === "sending" ? t.sending : t.submit}
                                </button>
                                <p className="text-center text-xs text-white/45">{SUPPORT_EMAIL}</p>
                            </form>
                        )}
                    </section>
                </div>
            </main>
        </PageFrame>
    );
}
